import { Center, Spinner, VStack } from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import View from "@pathwright/ui/src/components/ui/View"
import { PreloadableAdaptedSignedInContainer } from "@pathwright/features/src/modules/auth/LazyAdaptedSignedInContainer"
import OrderConfirmationContainer from "@pathwright/features/src/modules/order/OrderConfirmationContainer"
import { OrderSubscriptionFlow } from "@pathwright/features/src/modules/order/OrderSubscriptionCard"
import { getVisibilityOptionFromParams } from "account/subscription/utils"
import { ConnectStores } from "lib/core/store"
import ResourceOffering from "library/show/components/about/offerings/ResourceOffering"
import ResourceOfferings from "library/show/components/about/offerings/ResourceOfferings"
import { useEffect, useState } from "react"
import {
  REGISTER_STATE_AUTH,
  REGISTER_STATE_OFFERING_LIST,
  REGISTER_STATE_OFFERING_OPTIONS,
  REGISTER_STATE_PAYMENT,
  REGISTER_STATE_REGISTERABLE,
  REGISTER_STATE_REGISTERED,
  REGISTER_STATE_REGISTRATION_CLOSED,
  REGISTER_STATE_SUBSCRIBE,
  REGISTER_STATE_UNUSABLE_SUBSCRIPTION
} from "../constants"
import NextRegisterStateButton from "./NextRegisterStateButton"
import Register from "./Register"
import RegisterAuthGate from "./RegisterAuthGate"
import "./RegisterFlow.css"
import RegisterFlowHeader from "./RegisterFlowHeader"
import Registered from "./Registered"
import RegistrationClosed from "./RegistrationClosed"
import UnusableSubscription from "./UnusableSubscription"

// Register route onEnter: ensure both resource-store and register-store are loaded
async function handleRegisterMiddleware({ resourceSlug, cohortId }) {
  require("library/show/resource-store")
  require("library/register/register-store")
  await window.App.getStore("resource").action.selectResourceBySlug(
    resourceSlug,
    cohortId,
    "about"
  ).promise
}

const InnerRegisterFlow = (props) => {
  const {
    cohortId,
    resourceSlug,
    resource,
    offering,
    offeringOption,
    state,
    registration,
    orderId,
    match,
    alreadyRegistered,
    clearRegisterErrors,
    registering_outside_of_flow,
    flowError,
    isFull,
    staffContact,
    invitation,
    onClose,
    onOrderFulfilled,
    inviteToken,
    attemptToConvert = false,
    offerConversionActionLabel = ""
  } = props

  const redirectTo = window.location.href

  const setOffering = () => {
    if (registering_outside_of_flow) return

    handleRegisterMiddleware({
      resourceSlug,
      cohortId
    }).then(() => {
      const visibilityCodeObj = getVisibilityOptionFromParams()
      if (inviteToken) {
        // Prevent the modal from closing if this is an invitation
        // TODO: allow user to close modal?
        window.App.getStore("navigation").action.preventModalClose()
      }
      // Disable redirect for registered user if we're processing an order confirmation.
      const shouldAttemptRedirect = !orderId
      window.App.getStore("register").action.setOffering(
        cohortId ? parseInt(cohortId) : null,
        offeringOption ? parseInt(offeringOption) : null,
        inviteToken,
        visibilityCodeObj,
        shouldAttemptRedirect,
        attemptToConvert
      )
    })
  }

  useEffect(setOffering, [])

  // Improve UX slightly by preloading signed in component.
  useEffect(() => {
    PreloadableAdaptedSignedInContainer.preload()
  }, [])

  usePreviousEffect(
    ([prevOfferingOption, prevCohortId, prevState]) => {
      if (
        prevOfferingOption !== offeringOption ||
        prevCohortId !== cohortId ||
        // Store likely reset after sign-out.
        (prevState && !state)
      ) {
        setOffering()
      }
    },
    [offeringOption, cohortId, state, attemptToConvert]
  )

  return (
    <div className="RegisterFlow">
      {!!resource && (
        <RegisterFlowHeader
          offering={offering}
          resource={resource}
          invitation={invitation}
          onClose={onClose}
        />
      )}

      {flowError && <Alert error={flowError} onClear={clearRegisterErrors} />}

      <VStack
        minHeight="200px"
        w="full"
        h="full"
        justifyContent="flex-start"
        alignItems="stretch"
      >
        {orderId ? (
          <OrderConfirmationContainer
            orderId={orderId}
            onOrderFulfilled={onOrderFulfilled}
            renderActionButton={() => (
              <NextRegisterStateButton label={offerConversionActionLabel} />
            )}
          />
        ) : (
          (() => {
            switch (state) {
              case REGISTER_STATE_AUTH || REGISTER_STATE_REGISTERABLE:
                return (
                  <RegisterAuthGate
                    inviteToken={inviteToken}
                    redirectTo={redirectTo}
                    attemptToConvert={attemptToConvert}
                    offerConversionActionLabel={offerConversionActionLabel}
                  />
                )
              case REGISTER_STATE_OFFERING_LIST:
                return (
                  <View paddingTop>
                    <ResourceOfferings />
                  </View>
                )
              case REGISTER_STATE_OFFERING_OPTIONS:
                return (
                  <ResourceOffering offering={offering} resource={resource} />
                )
              case REGISTER_STATE_REGISTRATION_CLOSED:
                return (
                  <RegistrationClosed
                    offering={offering}
                    invitation={invitation}
                    isFull={isFull}
                    staffContact={staffContact}
                  />
                )
              case REGISTER_STATE_UNUSABLE_SUBSCRIPTION:
                return <UnusableSubscription />
              case REGISTER_STATE_SUBSCRIBE:
                return (
                  <OrderSubscriptionFlow
                    planIds={offering.include_in_subscription_plans}
                    productIds={offering.include_with_products}
                    redirectTo={redirectTo}
                  />
                )
              case REGISTER_STATE_PAYMENT:
              case REGISTER_STATE_REGISTERABLE:
                return (
                  <Register
                    attemptToConvert={attemptToConvert}
                    offerConversionActionLabel={offerConversionActionLabel}
                  />
                )
              case REGISTER_STATE_REGISTERED:
                return (
                  <Registered
                    registration={registration}
                    alreadyRegistered={alreadyRegistered}
                  />
                )
              default:
                return (
                  <Center w="full" h="full" flex={1}>
                    <Spinner />
                  </Center>
                )
            }
          })()
        )}
      </VStack>
    </div>
  )
}

InnerRegisterFlow.displayName = "InnerRegisterFlow"

const mapStateToProps = function (state, ownProps) {
  const {
    resource,
    offering,
    registration,
    already_registered,
    state: register_state,
    invitation,
    invitation_error,
    registration_error,
    registering_outside_of_flow
  } = state.registerStore.state
  const { is_authenticated } = state.authStore.state

  const { clearRegisterErrors, register } = state.registerStore.action

  // TODO: better error handling, better flow, better everything...
  const flowError = registration_error || invitation_error

  const staffContact = state.registerStore.request.offeringStaffContact?.()

  const seatsAvailable = state.resourceStore.request.seatsAvailable(
    offering,
    invitation
  )
  const isFull = !(seatsAvailable === -1 || seatsAvailable > 0)

  const onClose = state.registerStore.request.onCloseRegisterFlow
  const alreadyRegistered = already_registered

  const props = _.assign(
    {
      register,
      // state props
      resource,
      offering,
      registration,
      alreadyRegistered,
      state: register_state,
      staffContact,
      invitation,
      isFull,
      clearRegisterErrors,
      onClose,
      is_authenticated,
      registration_error,
      flowError,
      registering_outside_of_flow
    },
    ownProps
  )

  return props
}

const watch = [
  "invitation",
  "offering",
  // "resource",
  "state",
  "display_price",
  "registration",
  "already_registered",
  "invitation_error",
  "registration_error",
  "registering_outside_of_flow"
]

const stores = [
  { store: "register", watch },
  { store: "resource", watch: false },
  { store: "navigation", watch: false },
  { store: "auth", watch: ["is_authenticated"] }
]

const ConnectedInnerRegisterFlow = ConnectStores(
  InnerRegisterFlow,
  stores,
  {},
  mapStateToProps
)

// We've got to ensure resource and register store are loaded before rendering the register flow (which depends on the stores being setup to access things like *.request.*)
function RegisterFlow(props) {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    handleRegisterMiddleware({
      resourceSlug: props.resourceSlug,
      cohortId: props.cohortId
    }).then(() => {
      setLoading(false)
    })
  }, [])

  return loading ? (
    <Center w="full" h="full" flex={1}>
      <Spinner />
    </Center>
  ) : (
    <ConnectedInnerRegisterFlow {...props} />
  )
}

export default RegisterFlow
